/**
 * Leaderboard — public page showing top customers across 5 categories.
 * Admins get an extra "Pick winner of the month" panel here so they can
 * announce/prize it without going to a separate admin route.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth, api } from '../context/AuthContext';
import {
  Trophy, Crown, Users, Sparkles, Calendar, Medal, Star, Award,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  {
    key: 'top_visits_this_month',
    label: 'Top 5 This Month',
    icon: Calendar,
    accent: '#aaff00',
    blurb: 'Customers with the most completed visits this month.',
  },
  {
    key: 'most_loyal',
    label: 'Most Loyal',
    icon: Crown,
    accent: '#ffcc00',
    blurb: 'Lifetime visits — true ride-or-die clientele.',
  },
  {
    key: 'most_referrals',
    label: 'Most Referrals',
    icon: Users,
    accent: '#00d4ff',
    blurb: 'Clients who bring the most new faces in.',
  },
  {
    key: 'most_points_this_month',
    label: 'Most Points (This Month)',
    icon: Sparkles,
    accent: '#aaff00',
    blurb: 'Resets every month — feeds the auto-draw winner pick.',
  },
  {
    key: 'most_points',
    label: 'Most Current Points',
    icon: Sparkles,
    accent: '#cc99ff',
    blurb: 'Highest unredeemed loyalty balance right now — points ready to spend.',
  },
];

const rankColor = (idx) =>
  idx === 0 ? 'gold' :
  idx === 1 ? '#dcdcdc' :
  idx === 2 ? '#cd7f32' :
  'rgba(255,255,255,0.6)';

const rankBg = (idx) =>
  idx === 0 ? 'rgba(255,215,0,0.18)' :
  idx === 1 ? 'rgba(192,192,192,0.18)' :
  idx === 2 ? 'rgba(205,127,50,0.18)' :
  'rgba(255,255,255,0.05)';

const Leaderboard = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Admin "pick winner" state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [pickQuery, setPickQuery] = useState('');
  const [pickedUserId, setPickedUserId] = useState('');
  const [prize, setPrize] = useState('');
  const [note, setNote] = useState('');
  const [savingWinner, setSavingWinner] = useState(false);
  const [autoDrawing, setAutoDrawing] = useState(false);
  const [autoDrawPrize, setAutoDrawPrize] = useState('');

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await api.get('/api/leaderboard');
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  useEffect(() => {
    if (!pickerOpen || !isAdmin || allUsers.length > 0) return;
    (async () => {
      try {
        const res = await api.get('/api/admin/users');
        setAllUsers(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        toast.error('Failed to load users');
      }
    })();
  }, [pickerOpen, isAdmin, allUsers.length]);

  const filteredUsers = allUsers
    .filter(u => (u.role || 'customer') === 'customer')
    .filter(u => {
      const q = pickQuery.trim().toLowerCase();
      if (!q) return true;
      return (u.name || '').toLowerCase().includes(q)
          || (u.email || '').toLowerCase().includes(q)
          || (u.phone || '').toLowerCase().includes(q);
    })
    .slice(0, 30);

  const submitWinner = async () => {
    if (!pickedUserId) {
      toast.error('Pick a customer first');
      return;
    }
    setSavingWinner(true);
    try {
      const res = await api.post('/api/leaderboard/winner-of-month', {
        user_id: pickedUserId,
        prize: prize.trim() || null,
        note: note.trim() || null,
      });
      toast.success('Winner announced 🎉');
      setData((prev) => prev ? { ...prev, winner_of_month: res.data.winner } : prev);
      setPickerOpen(false);
      setPickedUserId('');
      setPrize('');
      setNote('');
      setPickQuery('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to announce winner');
    } finally {
      setSavingWinner(false);
    }
  };

  const clearWinner = async () => {
    if (!window.confirm('Clear this month\'s winner? The slot will show "TBD" again.')) return;
    try {
      await api.delete('/api/leaderboard/winner-of-month');
      setData((prev) => prev ? { ...prev, winner_of_month: null } : prev);
      toast.success('Winner cleared');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear winner');
    }
  };

  const autoDrawWinner = async () => {
    const hasExisting = !!data?.winner_of_month;
    const confirmMsg = hasExisting
      ? 'A winner is already set for this month. Replace them with the top point earner?'
      : 'Auto-draw this month\'s winner based on the most points earned this month?\n\nIf there\'s a tie at the top, the system will randomly pick one of the tied customers.';
    if (!window.confirm(confirmMsg)) return;
    setAutoDrawing(true);
    try {
      const res = await api.post('/api/leaderboard/winner-of-month/auto-draw', {
        prize: autoDrawPrize.trim() || null,
        overwrite: hasExisting,
      });
      setData((prev) => prev ? { ...prev, winner_of_month: res.data.winner } : prev);
      const tiedNote = res.data.tied_count > 1 ? ` (random pick from ${res.data.tied_count} tied)` : '';
      toast.success(`🎲 Auto-drawn: ${res.data.winner.name} — ${res.data.monthly_points} pts${tiedNote}`);
      setAutoDrawPrize('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to auto-draw winner');
    } finally {
      setAutoDrawing(false);
    }
  };

  return (
    <div className="sauce-sity-page" data-testid="leaderboard-page">
      <div className="sauce-sity-content">
        {/* Header */}
        <div className="ss-section-header ss-mb-48">
          <h1 className="ss-title-xl">
            <span className="ss-highlight-green">Sauce</span> Leaderboard
          </h1>
          <p className="ss-section-subtitle">
            Top of the chair — {data?.month || 'this month'}
          </p>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <p style={{ color: 'var(--ss-white-soft)' }}>Loading...</p>
          </div>
        )}

        {!loading && data && (
          <>
            {/* WINNER OF THE MONTH HERO */}
            <div data-testid="winner-hero" style={{
              background: data.winner_of_month
                ? 'radial-gradient(120% 80% at 50% 0%, rgba(255,215,0,0.20), rgba(170,0,255,0.18) 60%, rgba(0,0,0,0) 100%)'
                : 'rgba(255,255,255,0.03)',
              border: data.winner_of_month
                ? '1.5px solid rgba(255,215,0,0.55)'
                : '1px dashed rgba(255,255,255,0.18)',
              borderRadius: 22, padding: 28, marginBottom: 32, textAlign: 'center',
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
                <Trophy className="w-7 h-7" style={{ color: 'gold' }} />
                <span style={{
                  fontSize: '0.85rem', fontWeight: 800, color: 'gold',
                  textTransform: 'uppercase', letterSpacing: '0.18em',
                }}>
                  Winner of {data.month}
                </span>
              </div>
              {data.winner_of_month ? (
                <>
                  <h2 className="ss-title-lg" style={{ marginTop: 8, marginBottom: 6 }}>
                    🏆 {data.winner_of_month.name}
                  </h2>
                  {data.winner_of_month.auto_drawn && (
                    <span data-testid="winner-auto-drawn-badge" style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '3px 10px', fontSize: '0.7rem', fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      borderRadius: 999, background: 'rgba(170,255,0,0.15)',
                      color: '#aaff00', border: '1px solid rgba(170,255,0,0.4)',
                      marginBottom: 8,
                    }}>
                      🎲 Auto-drawn
                      {typeof data.winner_of_month.monthly_points === 'number' && (
                        <> · {data.winner_of_month.monthly_points} pts</>
                      )}
                    </span>
                  )}
                  {data.winner_of_month.prize && (
                    <p className="ss-text" style={{ color: '#fff', fontWeight: 600 }}>
                      🎁 {data.winner_of_month.prize}
                    </p>
                  )}
                  {data.winner_of_month.note && (
                    <p className="ss-text" style={{ opacity: 0.8 }}>{data.winner_of_month.note}</p>
                  )}
                </>
              ) : (
                <p style={{ color: 'var(--ss-white-soft)', fontSize: '0.95rem', margin: '8px 0 0 0' }}>
                  No winner announced yet for {data.month}.
                </p>
              )}

              {/* Admin actions */}
              {isAdmin && (
                <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="ss-btn ss-btn-primary"
                    onClick={autoDrawWinner}
                    disabled={autoDrawing}
                    data-testid="auto-draw-winner-btn"
                    title="Pick winner automatically based on most points earned this month. Random pick on tie."
                  >
                    <Sparkles className="w-4 h-4" />
                    {autoDrawing ? 'Drawing…' : (data.winner_of_month ? 'Re-draw (top monthly points)' : 'Auto-draw winner')}
                  </button>
                  <button
                    className="ss-btn ss-btn-secondary"
                    onClick={() => setPickerOpen(true)}
                    data-testid="open-pick-winner-btn"
                  >
                    <Award className="w-4 h-4" />
                    {data.winner_of_month ? 'Pick manually' : 'Pick winner manually'}
                  </button>
                  {data.winner_of_month && (
                    <button
                      className="ss-btn ss-btn-secondary"
                      style={{ borderColor: 'rgba(220,38,38,0.4)', color: '#fca5a5' }}
                      onClick={clearWinner}
                      data-testid="clear-winner-btn"
                    >
                      Clear winner
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Four category boards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 18,
            }}>
              {CATEGORIES.map((cat) => {
                const rows = data[cat.key] || [];
                const Icon = cat.icon;
                return (
                  <div key={cat.key} data-testid={`lb-board-${cat.key}`} style={{
                    background: 'rgba(0,0,0,0.45)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 18, padding: 20,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 12,
                        background: `${cat.accent}22`,
                        border: `1px solid ${cat.accent}55`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon className="w-5 h-5" style={{ color: cat.accent }} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, color: '#fff', fontSize: '1.05rem', fontWeight: 800 }}>{cat.label}</h3>
                        <p style={{ margin: 0, fontSize: '0.74rem', color: 'rgba(255,255,255,0.6)' }}>{cat.blurb}</p>
                      </div>
                    </div>
                    {rows.length === 0 ? (
                      <div style={{ padding: 14, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                        No qualifying customers yet — keep booking.
                      </div>
                    ) : (
                      rows.map((r, idx) => (
                        <div key={r.user_id || idx} data-testid={`lb-row-${cat.key}-${idx}`} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 8px',
                          borderRadius: 10,
                          background: rankBg(idx),
                          marginBottom: 6,
                          border: idx === 0 ? '1px solid rgba(255,215,0,0.35)' : 'none',
                        }}>
                          <span style={{
                            minWidth: 26, height: 26, borderRadius: 999,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(0,0,0,0.45)', color: rankColor(idx),
                            fontWeight: 800, fontSize: '0.85rem',
                          }}>
                            {idx + 1}
                          </span>
                          <span style={{
                            width: 34, height: 34, borderRadius: 999,
                            background: `linear-gradient(135deg, ${cat.accent}55, rgba(255,255,255,0.05))`,
                            color: '#fff', fontWeight: 800, fontSize: '0.85rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: `1px solid ${cat.accent}55`,
                          }}>
                            {r.initials}
                          </span>
                          <span style={{ flex: 1, color: '#fff', fontWeight: 600 }}>
                            {r.name}
                            {idx === 0 && (
                              <Star className="w-3.5 h-3.5" style={{ display: 'inline', marginLeft: 6, color: 'gold' }} />
                            )}
                          </span>
                          <span style={{ color: cat.accent, fontWeight: 800 }}>
                            {r.value.toLocaleString()}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Admin: pick winner modal */}
        {isAdmin && pickerOpen && (
          <div
            onClick={() => !savingWinner && setPickerOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
              zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            data-testid="pick-winner-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: 520, width: '100%',
                background: 'linear-gradient(160deg, rgba(40,0,60,0.95), rgba(15,15,15,0.95))',
                border: '1px solid rgba(255,215,0,0.4)',
                borderRadius: 20, padding: 24,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <Medal className="w-7 h-7" style={{ color: 'gold' }} />
                <h3 className="ss-title-md" style={{ margin: 0 }}>
                  Pick winner of {data?.month}
                </h3>
              </div>

              <label style={{ fontSize: '0.82rem', color: 'var(--ss-white-soft)' }}>Search customer</label>
              <input
                value={pickQuery}
                onChange={(e) => setPickQuery(e.target.value)}
                placeholder="Name, email, or phone…"
                style={{
                  width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                  background: 'rgba(0,0,0,0.5)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
                data-testid="pick-winner-search-input"
              />

              <div style={{
                marginTop: 10, maxHeight: 220, overflowY: 'auto',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
              }}>
                {filteredUsers.length === 0 ? (
                  <p style={{ padding: 14, color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: '0.85rem' }}>
                    No matching customers.
                  </p>
                ) : filteredUsers.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setPickedUserId(u.id)}
                    data-testid={`pick-winner-row-${u.email}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '10px 12px',
                      background: pickedUserId === u.id ? 'rgba(255,215,0,0.18)' : 'transparent',
                      border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)',
                      color: '#fff', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <div style={{
                      width: 30, height: 30, borderRadius: 999,
                      background: 'rgba(255,215,0,0.15)', color: 'gold',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
                    }}>
                      {(u.name || '?')[0]?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.name || '(no name)'}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.email}
                      </p>
                    </div>
                    {pickedUserId === u.id && <Star className="w-4 h-4" style={{ color: 'gold' }} />}
                  </button>
                ))}
              </div>

              <label style={{ display: 'block', marginTop: 16, fontSize: '0.82rem', color: 'var(--ss-white-soft)' }}>
                Prize (optional)
                <input
                  value={prize}
                  onChange={(e) => setPrize(e.target.value)}
                  placeholder="e.g. Free haircut + $50 gift card"
                  style={{
                    width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  data-testid="pick-winner-prize-input"
                />
              </label>
              <label style={{ display: 'block', marginTop: 12, fontSize: '0.82rem', color: 'var(--ss-white-soft)' }}>
                Public note (optional)
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Thank you for your loyalty!"
                  style={{
                    width: '100%', marginTop: 6, padding: '10px 12px', borderRadius: 10,
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                  data-testid="pick-winner-note-input"
                />
              </label>

              <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                <button
                  className="ss-btn ss-btn-secondary"
                  onClick={() => setPickerOpen(false)}
                  disabled={savingWinner}
                  data-testid="pick-winner-cancel-btn"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  className="ss-btn ss-btn-primary"
                  onClick={submitWinner}
                  disabled={savingWinner || !pickedUserId}
                  data-testid="pick-winner-submit-btn"
                  style={{ flex: 1 }}
                >
                  <Trophy className="w-4 h-4" />
                  {savingWinner ? 'Announcing…' : 'Announce winner'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
